import { buttonOuterRadiusPx, type ButtonSizeKey } from './button-radii';

/**
 * FLOATING BAR — the one surface for a bar of controls that floats OVER
 * content: the PDF scroll arrows and tool pills, the deck's bottom action
 * pills, the mind-map history pill, the quiz / oral-exam / case-study action
 * bars, the dashboard's sticky create bar.
 *
 * GLASS: the DS's glass — `secondary-50/70` over a 6px backdrop blur in light.
 * Dark keeps the same recipe: `secondary-50/70` is the SURFACE level
 * (elevation.ts), so a bar floating over tiles is never darker than them. `web:`-gated blur because
 * React Native has no `backdrop-filter` (native frost is `components/Glass`).
 */
export const FLOATING_BAR_GLASS =
  'border border-secondary-200 bg-secondary-50/70 web:backdrop-blur-[6px] web:shadow-lg';

/** The floating-bar glass for a single chip whose own dark fill would win
 *  (a DS control's `DARK_CONTROL`): the same surface, dark included. */
export const FLOATING_CHIP_GLASS = `${FLOATING_BAR_GLASS} dark:bg-secondary-50/70`;

/** The bar's border, part of the gap between its edge and its buttons' edge. */
export const FLOATING_BAR_BORDER_PX = 1;

/** Padding between the bar's border and its buttons, by button size. */
export const FLOATING_BAR_PADDING_PX: Record<ButtonSizeKey, number> = {
  xxs: 4,
  xs: 4,
  s: 4,
  m: 4,
  l: 6,
  xl: 6,
  xxl: 6,
};

/**
 * CONCENTRIC CORNERS — a bar's outer radius is its buttons' radius plus the
 * distance between the two edges (padding + border):
 *
 *   bar radius = button outer radius + padding + border
 *
 * With the rule, every bar's corners run parallel to the buttons inside it at
 * any size; a bar with a smaller radius pinches its corner buttons, a larger
 * one leaves a crescent.
 */
export function floatingBarRadiusPx(
  size: ButtonSizeKey,
  paddingPx: number = FLOATING_BAR_PADDING_PX[size],
): number {
  return buttonOuterRadiusPx(size) + paddingPx + FLOATING_BAR_BORDER_PX;
}

/**
 * Radius + padding for a bar holding buttons of `size` — the formula above,
 * spelled as literal classes so Tailwind sees them (`floating-bar.spec.ts`
 * pins every entry to the formula). A bar of mixed sizes keys on its LARGEST.
 */
export const FLOATING_BAR_SHAPE: Record<ButtonSizeKey, string> = {
  xxs: 'rounded-[17px] p-1',
  xs: 'rounded-[19px] p-1',
  s: 'rounded-[21px] p-1',
  m: 'rounded-[23px] p-1',
  l: 'rounded-[25px] p-1.5',
  xl: 'rounded-[27px] p-1.5',
  xxl: 'rounded-[29px] p-1.5',
};

/** The whole floating-bar surface for buttons of `size`: glass + concentric shape. */
export function floatingBarClass(size: ButtonSizeKey): string {
  return `${FLOATING_BAR_GLASS} ${FLOATING_BAR_SHAPE[size]}`;
}
